import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { projectsAPI, tasksAPI, employeesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const ProjectDetail = () => {
  const { can, user } = useAuth();
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [stats, setStats] = useState(null);
  const [changeLogs, setChangeLogs] = useState([]);
  const [taskChangeLogs, setTaskChangeLogs] = useState([]);
  const [projectLogsOpen, setProjectLogsOpen] = useState(true);
  const [taskLogsOpen, setTaskLogsOpen] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [allocationWarning, setAllocationWarning] = useState(null);
  const [taskDateError, setTaskDateError] = useState('');
  const [ragOverride, setRagOverride] = useState(false);
  const [ragStatus, setRagStatus] = useState('Green');
  const [ragNote, setRagNote] = useState('');
  const [ragSaving, setRagSaving] = useState(false);
  const [notes, setNotes] = useState([]);
  const [noteForm, setNoteForm] = useState({ note_type: 'General', content: '', author: 'Developer', task_ids: [] });
  const [noteSubmitting, setNoteSubmitting] = useState(false);
  const [taskFormData, setTaskFormData] = useState({
    task_name: '',
    description: '',
    parent_task_id: '',
    assigned_to: '',
    planned_start: '',
    planned_end: '',
    actual_start: '',
    actual_end: '',
    effort_days: '',
    progress: 0,
    status_id: 6,
    priority: 'Medium',
    allocation_percent: 100
  });

  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      // allSettled ensures one failing request never silently kills the whole page.
      // The project itself is required — everything else degrades gracefully.
      const [projectRes, statsRes, logsRes, tasksRes, employeesRes, notesRes, taskLogsRes] =
        await Promise.allSettled([
          projectsAPI.getById(id),
          projectsAPI.getStats(id),
          projectsAPI.getChangeLogs(id),
          tasksAPI.getAllByProject(id),
          employeesAPI.getAll(),
          projectsAPI.getNotes(id),
          projectsAPI.getTaskChangeLogs(id),
        ]);

      // Project is mandatory — if it failed, log and bail out
      if (projectRes.status === 'rejected') {
        console.error('Error fetching project:', projectRes.reason);
        return;
      }

      // Log any non-critical failures so they show in the console but don't crash the page
      [statsRes, logsRes, tasksRes, employeesRes, notesRes, taskLogsRes].forEach((r, i) => {
        if (r.status === 'rejected') {
          const names = ['stats', 'changeLogs', 'tasks', 'employees', 'notes', 'taskChangeLogs'];
          console.error(`Error fetching ${names[i]}:`, r.reason);
        }
      });

      setProject(projectRes.value.data);
      if (statsRes.status === 'fulfilled')     setStats(statsRes.value.data);
      if (logsRes.status === 'fulfilled')      setChangeLogs(logsRes.value.data);
      if (tasksRes.status === 'fulfilled')     setTasks(tasksRes.value.data);
      if (employeesRes.status === 'fulfilled') setEmployees(employeesRes.value.data);
      if (notesRes.status === 'fulfilled')     setNotes(notesRes.value.data);
      if (taskLogsRes.status === 'fulfilled')  setTaskChangeLogs(taskLogsRes.value.data);

      // Initialise RAG state from project
      setRagOverride(!!projectRes.value.data.rag_override);
      setRagStatus(projectRes.value.data.rag_status || projectRes.value.data.rag || 'Green');
      setRagNote(projectRes.value.data.rag_override_note || '');
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const dateOnly = dateString.substring(0, 10);
    const [year, month, day] = dateOnly.split('-');
    return `${day}/${month}/${year}`;
  };

  const handleTaskSubmit = async (e) => {
    e.preventDefault();
    setTaskDateError('');
    const { planned_start, planned_end, actual_start, actual_end } = taskFormData;

    if (planned_start && planned_end && planned_end < planned_start) {
      setTaskDateError('Planned end date cannot be earlier than planned start date.');
      return;
    }
    if (actual_start && actual_end && actual_end < actual_start) {
      setTaskDateError('Actual end date cannot be earlier than actual start date.');
      return;
    }
    if (Number(taskFormData.status_id) === 5 && (!actual_start || !actual_end)) {
      setTaskDateError('Actual start and actual end dates are required to mark a task as Completed.');
      return;
    }
    try {
      if (editingTaskId) {
        await tasksAPI.update(editingTaskId, { ...taskFormData, changed_by_id: user?.user_id, changed_by_name: user?.name });
      } else {
        await tasksAPI.create(id, taskFormData);
      }
      setShowTaskModal(false);
      resetTaskForm();
      fetchAllData();
    } catch (error) {
      console.error('Error saving task:', error);
      alert('Error saving task');
    }
  };

  const handleTaskEdit = (task) => {
    const formData = {
      task_name: task.task_name,
      description: task.description || '',
      parent_task_id: task.parent_task_id || '',
      assigned_to: task.assigned_to || '',
      planned_start: task.planned_start ? task.planned_start.substring(0, 10) : '',
      planned_end: task.planned_end ? task.planned_end.substring(0, 10) : '',
      actual_start: task.actual_start ? task.actual_start.substring(0, 10) : '',
      actual_end: task.actual_end ? task.actual_end.substring(0, 10) : '',
      forecast_start: task.forecast_start ? task.forecast_start.substring(0, 10) : '',
      forecast_end: task.forecast_end ? task.forecast_end.substring(0, 10) : '',
      effort_days: task.effort_days || '',
      progress: task.progress || 0,
      status_id: task.status_id || 6,
      priority: task.priority || 'Medium',
      allocation_percent: task.allocation_percent || 100
    };
    setTaskFormData(formData);
    setEditingTaskId(task.task_id);
    setShowTaskModal(true);
    // Check immediately using task.task_id directly since setEditingTaskId is async
    setTimeout(() => checkAllocationWithExclude(formData, task.task_id), 0);
  };

  const handleTaskDelete = async (taskId, taskName) => {
    if (window.confirm(`Delete task "${taskName}"?`)) {
      try {
        await tasksAPI.delete(taskId, { changed_by_id: user?.user_id, changed_by_name: user?.name });
        fetchAllData();
      } catch (error) {
        console.error('Error deleting task:', error);
        alert(error.response?.data?.error || 'Error deleting task');
      }
    }
  };

  const resetTaskForm = () => {
    setTaskFormData({
      task_name: '',
      description: '',
      parent_task_id: '',
      assigned_to: '',
      planned_start: '',
      planned_end: '',
      actual_start: '',
      actual_end: '',
      forecast_start: '',
      forecast_end: '',
      effort_days: '',
      progress: 0,
      status_id: 6,
      priority: 'Medium',
      allocation_percent: 100
    });
    setEditingTaskId(null);
    setAllocationWarning(null);
    setTaskDateError('');
  };

  const checkAllocationWithExclude = async (formData, excludeId) => {
    const { assigned_to, planned_start, planned_end, allocation_percent } = formData;
    if (!assigned_to || !planned_start || !planned_end || allocation_percent === '' || allocation_percent === null || allocation_percent === undefined) {
      setAllocationWarning(null);
      return;
    }
    try {
      const res = await employeesAPI.checkAllocation(assigned_to, {
        planned_start,
        planned_end,
        allocation_percent,
        exclude_task_id: excludeId || 0
      });
      if (res.data.is_overallocated) {
        setAllocationWarning(res.data);
      } else {
        setAllocationWarning(null);
      }
    } catch (error) {
      setAllocationWarning(null);
    }
  };

  const checkAllocation = async (formData) => {
    checkAllocationWithExclude(formData, editingTaskId || 0);
  };

  const handleTaskChange = (e) => {
    const updated = { ...taskFormData, [e.target.name]: e.target.value };
    setTaskFormData(updated);
    checkAllocation(updated);
  };

  const handleNoteSubmit = async (e) => {
    e.preventDefault();
    if (!noteForm.content.trim()) return;
    setNoteSubmitting(true);
    try {
      await projectsAPI.createNote(id, {
        note_type: noteForm.note_type,
        content: noteForm.content.trim(),
        author: noteForm.author || 'Developer',
        task_ids: noteForm.task_ids,
      });
      setNoteForm({ note_type: 'General', content: '', author: noteForm.author, task_ids: [] });
      await fetchAllData();
    } catch (error) {
      console.error('Error creating note:', error);
      alert('Error saving note');
    } finally {
      setNoteSubmitting(false);
    }
  };

  const handleNoteDelete = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await projectsAPI.deleteNote(noteId);
      setNotes(prev => prev.filter(n => n.note_id !== noteId));
    } catch (error) {
      console.error('Error deleting note:', error);
      alert('Error deleting note');
    }
  };

  const toggleNoteTask = (taskId) => {
    setNoteForm(prev => ({
      ...prev,
      task_ids: prev.task_ids.includes(taskId)
        ? prev.task_ids.filter(id => id !== taskId)
        : [...prev.task_ids, taskId],
    }));
  };

  const RAG_CONFIG = {
    Red:   { color: '#e74c3c', bg: '#fdf2f2', label: 'Red',   title: 'Project overdue' },
    Amber: { color: '#f39c12', bg: '#fef9ec', label: 'Amber', title: 'Task(s) overdue' },
    Green: { color: '#27ae60', bg: '#f0faf4', label: 'Green', title: 'On track' },
  };

  const RagDot = ({ rag, isOverride, size = 'normal' }) => {
    const cfg = RAG_CONFIG[rag] || RAG_CONFIG.Green;
    const dotSize = size === 'large' ? 14 : 11;
    const fontSize = size === 'large' ? '14px' : '12px';
    return (
      <span title={`${cfg.title}${isOverride ? ' (manual override)' : ''}`} style={{
        display: 'inline-flex', alignItems: 'center', gap: '6px',
        padding: size === 'large' ? '5px 14px' : '3px 9px',
        borderRadius: '20px', background: cfg.bg,
        border: `1px solid ${cfg.color}`, fontSize, fontWeight: '600', color: cfg.color,
        whiteSpace: 'nowrap', cursor: 'default',
      }}>
        <span style={{ width: dotSize, height: dotSize, borderRadius: '50%', background: cfg.color, display: 'inline-block', flexShrink: 0 }} />
        {cfg.label}
        {isOverride && <span style={{ fontSize: '11px', opacity: 0.7 }}>✎</span>}
      </span>
    );
  };

  const saveRag = async () => {
    setRagSaving(true);
    try {
      await projectsAPI.update(id, {
        project_name: project.project_name,
        description: project.description,
        customer_name: project.customer_name,
        manager_id: project.manager_id,
        actual_start_date: project.actual_start_date,
        actual_end_date: project.actual_end_date,
        status: project.status,
        priority: project.priority,
        rag_override: ragOverride,
        rag_status: ragStatus,
        rag_override_note: ragNote,
        changed_by_id: user?.user_id,
        changed_by_name: user?.name,
      });
      await fetchAllData();
    } catch (error) {
      console.error('Error saving RAG:', error);
      alert('Error saving RAG status');
    } finally {
      setRagSaving(false);
    }
  };

  const getStatusBadge = (statusId, statusName) => {
    const statusColors = {
      6: 'badge-info',     // Not Started
      2: 'badge-warning',  // In Progress
      5: 'badge-success',  // Completed
      3: 'badge-warning'   // On Hold
    };
    return <span className={`badge ${statusColors[statusId] || 'badge-info'}`}>{statusName}</span>;
  };

  if (loading) {
    return <div className="loading">Loading project...</div>;
  }

  if (!project) {
    return <div className="loading">Project not found</div>;
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <h2 className="page-title">{project.project_name}</h2>
            <RagDot rag={project.rag} isOverride={project.rag_is_override} size="large" />
          </div>
          <p className="page-subtitle">{project.description || 'No description'}</p>
        </div>
        <Link to="/projects" className="btn btn-secondary">
          ← Back to Projects
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-3">
        <div className="stat-card">
          <div className="stat-card-header">
            <div>
              <div className="stat-card-value">{stats?.total_tasks || 0}</div>
              <div className="stat-card-label">Total Tasks</div>
            </div>
            <div className="stat-card-icon blue">✓</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div>
              <div className="stat-card-value">{stats?.completed_tasks || 0}</div>
              <div className="stat-card-label">Completed</div>
            </div>
            <div className="stat-card-icon green">✅</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div>
              <div className="stat-card-value">{stats?.in_progress_tasks || 0}</div>
              <div className="stat-card-label">In Progress</div>
            </div>
            <div className="stat-card-icon orange">⏳</div>
          </div>
        </div>
      </div>

      {/* Project Information */}
      <div className="card" style={{ marginTop: '25px' }}>
        <div className="card-header">
          <h3 className="card-title">Project Information</h3>
        </div>
        <div style={{ padding: '20px' }}>
          <div className="grid grid-2" style={{ gap: '20px' }}>
            <div>
              <strong style={{ color: '#7f8c8d', fontSize: '13px', display: 'block', marginBottom: '5px' }}>Customer</strong>
              <div style={{ fontSize: '15px' }}>{project.customer_name || 'Not specified'}</div>
            </div>
            <div>
              <strong style={{ color: '#7f8c8d', fontSize: '13px', display: 'block', marginBottom: '5px' }}>Project Manager</strong>
              <div style={{ fontSize: '15px' }}>{project.manager_name || 'Not assigned'}</div>
            </div>
            <div>
              <strong style={{ color: '#7f8c8d', fontSize: '13px', display: 'block', marginBottom: '5px' }}>Planned Dates</strong>
              <div style={{ fontSize: '15px' }}>{formatDate(project.planned_start_date)} → {formatDate(project.planned_end_date)}</div>
            </div>
            <div>
              <strong style={{ color: '#7f8c8d', fontSize: '13px', display: 'block', marginBottom: '5px' }}>Actual Dates</strong>
              <div style={{ fontSize: '15px' }}>{formatDate(project.actual_start_date)} → {formatDate(project.actual_end_date)}</div>
            </div>
          </div>

          {/* RAG Status Panel */}
          <div style={{ marginTop: '20px', padding: '16px', background: '#f8f9fa', borderRadius: '10px', border: '1px solid #e1e8ed' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
              <strong style={{ color: '#2c3e50', fontSize: '14px' }}>🚦 RAG Status</strong>
              <RagDot rag={project.rag} isOverride={project.rag_is_override} />
              {project.rag_is_override && project.rag_override_note && (
                <span style={{ fontSize: '13px', color: '#7f8c8d', fontStyle: 'italic' }}>"{project.rag_override_note}"</span>
              )}
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', color: '#2c3e50', marginBottom: ragOverride ? '12px' : '0' }}>
              <input
                type="checkbox"
                checked={ragOverride}
                onChange={(e) => setRagOverride(e.target.checked)}
                style={{ width: '15px', height: '15px', cursor: 'pointer' }}
              />
              Override RAG manually
            </label>
            {ragOverride && (
              <div style={{ paddingLeft: '24px' }}>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
                  {['Green', 'Amber', 'Red'].map(val => {
                    const cfg = { Green: { color: '#27ae60', bg: '#f0faf4' }, Amber: { color: '#f39c12', bg: '#fef9ec' }, Red: { color: '#e74c3c', bg: '#fdf2f2' } }[val];
                    const selected = ragStatus === val;
                    return (
                      <label key={val} style={{
                        display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer',
                        padding: '7px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '600',
                        border: `2px solid ${selected ? cfg.color : '#ddd'}`,
                        background: selected ? cfg.bg : '#fff',
                        color: selected ? cfg.color : '#7f8c8d',
                        transition: 'all 0.15s'
                      }}>
                        <input type="radio" name="detail_rag" value={val} checked={selected} onChange={() => setRagStatus(val)} style={{ display: 'none' }} />
                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: cfg.color, display: 'inline-block' }} />
                        {val}
                      </label>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Reason for override (optional)"
                    value={ragNote}
                    onChange={(e) => setRagNote(e.target.value)}
                    style={{ fontSize: '13px', flex: 1 }}
                  />
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={saveRag}
                    disabled={ragSaving}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {ragSaving ? 'Saving…' : '💾 Save RAG'}
                  </button>
                </div>
              </div>
            )}
            {!ragOverride && project.rag_is_override && (
              <div style={{ paddingLeft: '24px', marginTop: '4px' }}>
                <button className="btn btn-sm btn-secondary" onClick={saveRag} disabled={ragSaving}>
                  {ragSaving ? 'Saving…' : '↩ Clear override & revert to auto'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tasks Section */}
      <div className="card" style={{ marginTop: '25px' }}>
        <div className="card-header">
          <h3 className="card-title">📋 Tasks ({tasks.length})</h3>
          {can('createEditProject') && (
          <button
            className="btn btn-primary btn-sm"
            onClick={() => { resetTaskForm(); setShowTaskModal(true); }}
          >
            ➕ New Task
          </button>
          )}
        </div>
        <div className="table-container">
          {tasks.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📋</div>
              <div className="empty-state-text">No tasks yet</div>
              {can('createEditProject') && (
              <button className="btn btn-primary" onClick={() => setShowTaskModal(true)} style={{ marginTop: '20px' }}>
                Create First Task
              </button>
              )}
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Task Name</th>
                  <th>Assigned To</th>
                  <th>Dates</th>
                  <th>Progress</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th style={{ minWidth: '200px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((task) => (
                  <tr key={task.task_id}>
                    <td>
                      <strong>{task.task_name}</strong>
                      {task.description && (
                        <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '4px' }}>
                          {task.description.substring(0, 50)}...
                        </div>
                      )}
                    </td>
                    <td>{task.assigned_to_name || 'Unassigned'}</td>
                    <td>
                      <div style={{ fontSize: '13px' }}>
                        {formatDate(task.planned_start)} → {formatDate(task.planned_end)}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div className="progress" style={{ flex: 1, minWidth: '80px' }}>
                          <div
                            className={`progress-bar ${
                              task.progress >= 75 ? 'success' :
                              task.progress >= 50 ? 'warning' :
                              task.progress >= 25 ? 'orange' : 'danger'
                            }`}
                            style={{ width: `${task.progress}%` }}
                          />
                        </div>
                        <span style={{ fontSize: '13px', fontWeight: '600', minWidth: '35px' }}>
                          {task.progress}%
                        </span>
                      </div>
                    </td>
                    <td>{getStatusBadge(task.status_id, task.status_name)}</td>
                    <td>
                      <span className={`badge ${
                        task.priority === 'High' || task.priority === 'Critical' ? 'badge-danger' :
                        task.priority === 'Medium' ? 'badge-warning' : 'badge-info'
                      }`}>
                        {task.priority}
                      </span>
                    </td>
                    <td style={{ whiteSpace: 'nowrap', minWidth: '200px' }}>
                      {can('createEditTask') && (
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handleTaskEdit(task)}
                        style={{ marginRight: '8px' }}
                      >
                        ✏️ Edit
                      </button>
                      )}
                      {can('createEditTask') && (
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleTaskDelete(task.task_id, task.task_name)}
                      >
                        🗑️ Delete
                      </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Developer Notes */}
      {(() => {
        const NOTE_TYPE_CONFIG = {
          General:     { color: '#3498db', bg: '#eaf4fd', icon: '📝' },
          Progress:    { color: '#27ae60', bg: '#eafaf1', icon: '📈' },
          Delay:       { color: '#f39c12', bg: '#fef9ec', icon: '⏰' },
          Bottleneck:  { color: '#e74c3c', bg: '#fdf2f2', icon: '🚧' },
        };

        return (
          <div className="card" style={{ marginTop: '25px' }}>
            <div className="card-header">
              <h3 className="card-title">🗒️ Developer Notes</h3>
              <span style={{ fontSize: '13px', color: '#7f8c8d' }}>
                {notes.length} note{notes.length !== 1 ? 's' : ''}
              </span>
            </div>

            {/* Add Note Form */}
            <form onSubmit={handleNoteSubmit} style={{ padding: '20px', borderBottom: '2px solid #f0f3f7', background: '#fafbfc', borderRadius: '0' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 160px', gap: '12px', marginBottom: '12px' }}>
                {/* Note Type */}
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#7f8c8d', marginBottom: '5px', textTransform: 'uppercase' }}>Type</label>
                  <select
                    className="form-control"
                    value={noteForm.note_type}
                    onChange={(e) => setNoteForm(prev => ({ ...prev, note_type: e.target.value }))}
                    style={{ fontSize: '13px', padding: '9px 12px' }}
                  >
                    <option value="General">📝 General</option>
                    <option value="Progress">📈 Progress</option>
                    <option value="Delay">⏰ Delay</option>
                    <option value="Bottleneck">🚧 Bottleneck</option>
                  </select>
                </div>
                {/* Content */}
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#7f8c8d', marginBottom: '5px', textTransform: 'uppercase' }}>Note *</label>
                  <textarea
                    className="form-control"
                    placeholder="What's the update, delay, or blocker?"
                    value={noteForm.content}
                    onChange={(e) => setNoteForm(prev => ({ ...prev, content: e.target.value }))}
                    rows={2}
                    required
                    style={{ fontSize: '13px', resize: 'vertical', minHeight: '60px', padding: '9px 12px' }}
                  />
                </div>
                {/* Author */}
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#7f8c8d', marginBottom: '5px', textTransform: 'uppercase' }}>Author</label>
                  <input
                    type="text"
                    className="form-control"
                    value={noteForm.author}
                    onChange={(e) => setNoteForm(prev => ({ ...prev, author: e.target.value }))}
                    placeholder="Developer"
                    style={{ fontSize: '13px', padding: '9px 12px' }}
                  />
                </div>
              </div>

              {/* Task Tags */}
              {tasks.length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#7f8c8d', marginBottom: '7px', textTransform: 'uppercase' }}>Tag Tasks (optional)</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '7px' }}>
                    {tasks.map(task => {
                      const selected = noteForm.task_ids.includes(task.task_id);
                      return (
                        <button
                          key={task.task_id}
                          type="button"
                          onClick={() => toggleNoteTask(task.task_id)}
                          style={{
                            padding: '4px 12px',
                            borderRadius: '20px',
                            border: `1.5px solid ${selected ? '#3498db' : '#dde1e7'}`,
                            background: selected ? '#eaf4fd' : '#fff',
                            color: selected ? '#2980b9' : '#7f8c8d',
                            fontSize: '12px',
                            fontWeight: selected ? '600' : '400',
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                          }}
                        >
                          {selected ? '✓ ' : ''}{task.task_name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <button
                type="submit"
                className="btn btn-primary btn-sm"
                disabled={noteSubmitting || !noteForm.content.trim()}
                style={{ minWidth: '120px' }}
              >
                {noteSubmitting ? 'Saving…' : '➕ Add Note'}
              </button>
            </form>

            {/* Notes List */}
            {notes.length === 0 ? (
              <div style={{ padding: '30px 20px', textAlign: 'center', color: '#95a5a6', fontSize: '14px' }}>
                No notes yet — add the first one above.
              </div>
            ) : (
              <div style={{ padding: '12px 20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {notes.map(note => {
                  const cfg = NOTE_TYPE_CONFIG[note.note_type] || NOTE_TYPE_CONFIG.General;
                  return (
                    <div key={note.note_id} style={{
                      padding: '14px 16px',
                      borderRadius: '10px',
                      border: `1px solid ${cfg.color}22`,
                      background: cfg.bg,
                      borderLeft: `4px solid ${cfg.color}`,
                      position: 'relative',
                    }}>
                      {/* Header row */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: '5px',
                          padding: '2px 10px', borderRadius: '20px',
                          background: cfg.color, color: '#fff',
                          fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.4px'
                        }}>
                          {cfg.icon} {note.note_type}
                        </span>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#2c3e50' }}>{note.author}</span>
                        <span style={{ fontSize: '12px', color: '#95a5a6' }}>
                          {new Date(note.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {/* Tagged tasks */}
                        {note.tagged_tasks && note.tagged_tasks.length > 0 && (
                          <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', marginLeft: '4px' }}>
                            {note.tagged_tasks.map(t => (
                              <span key={t.task_id} style={{
                                padding: '2px 10px', borderRadius: '20px',
                                background: '#e8f4fd', border: '1px solid #aed6f1',
                                color: '#2471a3', fontSize: '11px', fontWeight: '600',
                              }}>
                                🔗 {t.task_name}
                              </span>
                            ))}
                          </div>
                        )}
                        {/* Delete button */}
                        <button
                          onClick={() => handleNoteDelete(note.note_id)}
                          style={{
                            marginLeft: 'auto', background: 'none', border: 'none',
                            cursor: 'pointer', color: '#bdc3c7', fontSize: '16px',
                            lineHeight: 1, padding: '2px 4px', borderRadius: '4px',
                            transition: 'color 0.15s',
                          }}
                          onMouseEnter={e => e.target.style.color = '#e74c3c'}
                          onMouseLeave={e => e.target.style.color = '#bdc3c7'}
                          title="Delete note"
                        >
                          ×
                        </button>
                      </div>
                      {/* Note content */}
                      <p style={{ margin: 0, fontSize: '14px', color: '#2c3e50', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                        {note.content}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* Change History */}
      {(changeLogs.length > 0 || taskChangeLogs.length > 0) && (() => {
        const dateFields = ['actual_start', 'actual_end', 'actual_start_date', 'actual_end_date', 'forecast_start', 'forecast_end'];
        const boolFields = { is_unplanned: ['Planned', 'Unplanned'], on_hold: ['Active', 'On Hold'] };
        const fieldLabels = {
          actual_start_date: 'Actual Start',
          actual_end_date:   'Actual End',
          rag_status:        'RAG Status',
          rag_override_note: 'RAG Note',
          task_deleted:      'Task Deleted',
          actual_start:      'Actual Start',
          actual_end:        'Actual End',
          forecast_start:    'Forecast Start',
          forecast_end:      'Forecast End',
          is_unplanned:      'Unplanned Flag',
          on_hold:           'On Hold',
        };
        const formatField = (field) => fieldLabels[field] || field;
        const formatValue = (field, value) => {
          if (value === null || value === undefined || value === '') return '(empty)';
          if (boolFields[field]) return boolFields[field][Number(value)] || value;
          if (dateFields.includes(field)) return formatDate(value);
          return value;
        };

        // Sort helpers
        const sortedProjectLogs = [...changeLogs].sort((a, b) => new Date(b.changed_at) - new Date(a.changed_at));
        const sortedTaskLogs = [...taskChangeLogs].sort((a, b) => new Date(b.changed_at) - new Date(a.changed_at));

        const accordionHeaderStyle = {
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '14px 20px',
          cursor: 'pointer',
          borderRadius: '8px',
          userSelect: 'none',
          transition: 'background 0.2s',
        };

        const chevron = (open) => (
          <span style={{ fontSize: '13px', color: '#7f8c8d', transition: 'transform 0.2s', display: 'inline-block', transform: open ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
        );

        return (
          <div className="card" style={{ marginTop: '25px' }}>
            <div className="card-header">
              <h3 className="card-title">📋 Change History</h3>
              <div style={{ fontSize: '13px', color: '#7f8c8d' }}>
                {changeLogs.length + taskChangeLogs.length} change{(changeLogs.length + taskChangeLogs.length) !== 1 ? 's' : ''} recorded
              </div>
            </div>

            {/* Project Change Logs Accordion */}
            <div style={{ borderBottom: '1px solid #ecf0f1' }}>
              <div
                style={{ ...accordionHeaderStyle, background: projectLogsOpen ? '#f8f9fa' : '#fff' }}
                onClick={() => setProjectLogsOpen(!projectLogsOpen)}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                onMouseLeave={(e) => e.currentTarget.style.background = projectLogsOpen ? '#f8f9fa' : '#fff'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {chevron(projectLogsOpen)}
                  <span style={{ fontWeight: '600', fontSize: '14px', color: '#2c3e50' }}>Project Changes</span>
                  <span className="badge badge-primary" style={{ fontSize: '11px' }}>{changeLogs.length}</span>
                </div>
                <span style={{ fontSize: '12px', color: '#95a5a6' }}>
                  {changeLogs.length === 0 ? 'No project-level changes' : `Last: ${new Date(sortedProjectLogs[0].changed_at).toLocaleDateString('en-GB')}`}
                </span>
              </div>

              {projectLogsOpen && (
                <div className="table-container" style={{ padding: '0 0 8px 0' }}>
                  {changeLogs.length === 0 ? (
                    <div style={{ padding: '20px 24px', color: '#95a5a6', fontSize: '14px' }}>No project-level changes recorded yet.</div>
                  ) : (
                    <table>
                      <thead>
                        <tr>
                          <th>Date & Time</th>
                          <th>Field Changed</th>
                          <th>Old Value</th>
                          <th>New Value</th>
                          <th>Changed By</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedProjectLogs.map((log) => (
                          <tr key={`p-${log.log_id}`}>
                            <td style={{ whiteSpace: 'nowrap' }}>{new Date(log.changed_at).toLocaleString('en-GB')}</td>
                            <td><strong>{formatField(log.field_name)}</strong></td>
                            <td style={{ color: '#e74c3c' }}>{formatValue(log.field_name, log.old_value)}</td>
                            <td style={{ color: '#27ae60' }}>{formatValue(log.field_name, log.new_value)}</td>
                            <td style={{ fontSize: '13px', color: '#64748b' }}>{log.changed_by_name || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>

            {/* Task Change Logs Accordion */}
            <div>
              <div
                style={{ ...accordionHeaderStyle, background: taskLogsOpen ? '#f8f9fa' : '#fff' }}
                onClick={() => setTaskLogsOpen(!taskLogsOpen)}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                onMouseLeave={(e) => e.currentTarget.style.background = taskLogsOpen ? '#f8f9fa' : '#fff'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {chevron(taskLogsOpen)}
                  <span style={{ fontWeight: '600', fontSize: '14px', color: '#2c3e50' }}>Task Changes</span>
                  <span className="badge badge-info" style={{ fontSize: '11px' }}>{taskChangeLogs.length}</span>
                </div>
                <span style={{ fontSize: '12px', color: '#95a5a6' }}>
                  {taskChangeLogs.length === 0 ? 'No task changes' : `Last: ${new Date(sortedTaskLogs[0].changed_at).toLocaleDateString('en-GB')}`}
                </span>
              </div>

              {taskLogsOpen && (
                <div className="table-container" style={{ padding: '0 0 8px 0' }}>
                  {taskChangeLogs.length === 0 ? (
                    <div style={{ padding: '20px 24px', color: '#95a5a6', fontSize: '14px' }}>No task changes recorded yet.</div>
                  ) : (
                    <table>
                      <thead>
                        <tr>
                          <th>Date & Time</th>
                          <th>Task</th>
                          <th>Field Changed</th>
                          <th>Old Value</th>
                          <th>New Value</th>
                          <th>Changed By</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedTaskLogs.map((log) => (
                          <tr key={`t-${log.log_id}-${log.task_id}`}>
                            <td style={{ whiteSpace: 'nowrap' }}>{new Date(log.changed_at).toLocaleString('en-GB')}</td>
                            <td>
                              <span className="badge badge-info" style={{ fontSize: '11px' }} title={log.task_name}>
                                {log.task_name.length > 22 ? log.task_name.substring(0, 22) + '…' : log.task_name}
                              </span>
                            </td>
                            <td><strong>{formatField(log.field_name)}</strong></td>
                            <td style={{ color: '#e74c3c' }}>{formatValue(log.field_name, log.old_value)}</td>
                            <td style={{ color: '#27ae60' }}>{formatValue(log.field_name, log.new_value)}</td>
                            <td style={{ fontSize: '13px', color: '#64748b' }}>{log.changed_by_name || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>

          </div>
        );
      })()}

      {/* Task Modal */}
      {showTaskModal && (
        <div className="modal-overlay" onClick={() => { setShowTaskModal(false); resetTaskForm(); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h3 className="modal-title">{editingTaskId ? 'Edit Task' : 'New Task'}</h3>
            </div>
            <form onSubmit={handleTaskSubmit}>
              <div className="form-group">
                <label className="form-label">Task Name *</label>
                <input
                  type="text"
                  name="task_name"
                  className="form-control"
                  value={taskFormData.task_name}
                  onChange={handleTaskChange}
                  required
                  placeholder="Enter task name"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  name="description"
                  className="form-control"
                  value={taskFormData.description}
                  onChange={handleTaskChange}
                  rows="3"
                  placeholder="Task description..."
                />
              </div>

              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Assigned To</label>
                  <select
                    name="assigned_to"
                    className="form-control"
                    value={taskFormData.assigned_to}
                    onChange={handleTaskChange}
                  >
                    <option value="">Select Employee</option>
                    {employees.map((emp) => (
                      <option key={emp.employee_id} value={emp.employee_id}>
                        {emp.employee_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Priority</label>
                  <select
                    name="priority"
                    className="form-control"
                    value={taskFormData.priority}
                    onChange={handleTaskChange}
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Critical">Critical</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Planned Start {editingTaskId && '🔒'}</label>
                  <input
                    type="date"
                    name="planned_start"
                    className="form-control"
                    value={taskFormData.planned_start}
                    onChange={handleTaskChange}
                    disabled={!!editingTaskId}
                    style={editingTaskId ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
                  />
                  {editingTaskId && <small style={{ color: '#7f8c8d', fontSize: '11px' }}>Planned dates cannot be changed after creation</small>}
                </div>

                <div className="form-group">
                  <label className="form-label">Planned End {editingTaskId && '🔒'}</label>
                  <input
                    type="date"
                    name="planned_end"
                    className="form-control"
                    value={taskFormData.planned_end}
                    onChange={handleTaskChange}
                    disabled={!!editingTaskId}
                    style={editingTaskId ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
                  />
                </div>
              </div>

              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label" style={{ color: Number(taskFormData.status_id) === 5 && !taskFormData.actual_start ? '#e74c3c' : undefined }}>
                    Actual Start {Number(taskFormData.status_id) === 5 && <span style={{ color: '#e74c3c' }}>*</span>}
                  </label>
                  <input
                    type="date"
                    name="actual_start"
                    className="form-control"
                    value={taskFormData.actual_start}
                    onChange={handleTaskChange}
                    style={Number(taskFormData.status_id) === 5 && !taskFormData.actual_start ? { borderColor: '#e74c3c', background: '#fef2f2' } : undefined}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" style={{ color: Number(taskFormData.status_id) === 5 && !taskFormData.actual_end ? '#e74c3c' : undefined }}>
                    Actual End {Number(taskFormData.status_id) === 5 && <span style={{ color: '#e74c3c' }}>*</span>}
                  </label>
                  <input
                    type="date"
                    name="actual_end"
                    className="form-control"
                    value={taskFormData.actual_end}
                    onChange={handleTaskChange}
                    style={Number(taskFormData.status_id) === 5 && !taskFormData.actual_end ? { borderColor: '#e74c3c', background: '#fef2f2' } : undefined}
                  />
                </div>
              </div>

              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Allocation %</label>
                  <input
                    type="number"
                    name="allocation_percent"
                    className="form-control"
                    value={taskFormData.allocation_percent}
                    onChange={handleTaskChange}
                    min="0"
                    max="100"
                  />
                </div>
              </div>

              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    name="status_id"
                    className="form-control"
                    value={taskFormData.status_id}
                    onChange={handleTaskChange}
                  >
                    <option value="6">Not Started</option>
                    <option value="2">In Progress</option>
                    <option value="5">Completed</option>
                    <option value="3">On Hold</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Progress %</label>
                  <input
                    type="number"
                    name="progress"
                    className="form-control"
                    value={taskFormData.progress}
                    onChange={handleTaskChange}
                    min="0"
                    max="100"
                  />
                </div>
              </div>

              {/* Forecast Start — always visible when editing */}
              {editingTaskId && (
                <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px', padding: '14px 16px', marginBottom: '12px' }}>
                  <label className="form-label" style={{ color: '#16a34a', marginBottom: '8px', display: 'block' }}>
                    🚀 Forecast Start
                    <span style={{ fontWeight: '400', color: '#166534', fontSize: '11px', marginLeft: '8px' }}>When do you expect this task to start?</span>
                  </label>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <input
                        type="date"
                        name="forecast_start"
                        className="form-control"
                        value={taskFormData.forecast_start}
                        onChange={handleTaskChange}
                        style={{ borderColor: '#86efac' }}
                      />
                      {taskFormData.forecast_start && taskFormData.planned_start && (
                        <small style={{ color: '#166534', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                          Planned start {taskFormData.planned_start} · {taskFormData.forecast_start < taskFormData.planned_start
                            ? `starting ${Math.round((new Date(taskFormData.planned_start + 'T12:00:00') - new Date(taskFormData.forecast_start + 'T12:00:00')) / 86400000)} day(s) early`
                            : `starting ${Math.round((new Date(taskFormData.forecast_start + 'T12:00:00') - new Date(taskFormData.planned_start + 'T12:00:00')) / 86400000)} day(s) late`}
                        </small>
                      )}
                    </div>
                    <small style={{ color: '#166534', fontSize: '11px', lineHeight: '1.5', maxWidth: '200px', paddingTop: '8px' }}>
                      📌 Planned Start is locked — this sets a new expected start date without changing the original commitment.
                    </small>
                  </div>
                </div>
              )}

              {/* Forecast End — always visible when editing */}
              {editingTaskId && (
                <div style={{ background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: '8px', padding: '14px 16px', marginBottom: '16px' }}>
                  <label className="form-label" style={{ color: '#d97706', marginBottom: '8px', display: 'block' }}>
                    🕐 Forecast End
                    <span style={{ fontWeight: '400', color: '#92400e', fontSize: '11px', marginLeft: '8px' }}>When do you expect this task to finish?</span>
                  </label>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <input
                        type="date"
                        name="forecast_end"
                        className="form-control"
                        value={taskFormData.forecast_end}
                        onChange={handleTaskChange}
                        style={{ borderColor: '#f59e0b' }}
                      />
                      {taskFormData.forecast_end && taskFormData.planned_end && (
                        <small style={{ color: '#78350f', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                          Originally due {taskFormData.planned_end} · forecast slips by {Math.round((new Date(taskFormData.forecast_end + 'T12:00:00') - new Date(taskFormData.planned_end + 'T12:00:00')) / 86400000)} day(s)
                        </small>
                      )}
                    </div>
                    <small style={{ color: '#92400e', fontSize: '11px', lineHeight: '1.5', maxWidth: '200px', paddingTop: '8px' }}>
                      📌 Planned End is locked — this sets a new expected completion date without changing the original commitment.
                    </small>
                  </div>
                </div>
              )}

              {/* Allocation Warning */}
              {allocationWarning && (
                <div style={{
                  margin: '0 0 16px 0',
                  padding: '14px 16px',
                  background: '#fffbeb',
                  border: '1px solid #f59e0b',
                  borderRadius: '8px',
                  borderLeft: '4px solid #f59e0b'
                }}>
                  <div style={{ fontWeight: '600', color: '#92400e', marginBottom: '6px', fontSize: '14px' }}>
                    ⚠️ Over-allocation Warning — Total: {allocationWarning.total_allocation}%
                  </div>
                  <div style={{ fontSize: '13px', color: '#78350f', marginBottom: '8px' }}>
                    This employee is already allocated {allocationWarning.existing_allocation}% during this period. Adding {taskFormData.allocation_percent}% brings the total to {allocationWarning.total_allocation}%. You can still save.
                  </div>
                  {allocationWarning.conflicting_tasks.length > 0 && (
                    <div style={{ fontSize: '12px', color: '#92400e' }}>
                      <strong>Conflicting tasks:</strong>{' '}
                      {allocationWarning.conflicting_tasks.map((t, i) => (
                        <span key={t.task_id}>
                          {t.task_name} ({t.project_name} — {t.allocation_percent}%)
                          {i < allocationWarning.conflicting_tasks.length - 1 ? ', ' : ''}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {taskDateError && (
                <div style={{ margin: '0 0 16px 0', padding: '12px 16px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', borderLeft: '4px solid #e74c3c', color: '#991b1b', fontSize: '14px', fontWeight: '500' }}>
                  ❌ {taskDateError}
                </div>
              )}

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => { setShowTaskModal(false); resetTaskForm(); }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingTaskId ? '💾 Update' : '➕ Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetail;
